import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// Utils
import { useAppState } from '../../context/AppContext'

// Styles
import { shadows } from '../../styles/effects'

// Components
import Text from '../Text'
import { Icon } from '../icon'
import Button, { ButtonVariant } from '../Button'

// Types
import {
  EvaluationAssessmentType,
  ISingleEvaluationPure,
} from '../../common/models/Evaluation'

interface IProps {
  type: EvaluationAssessmentType
  assessment: ISingleEvaluationPure
  isSubmitted?: boolean
  onClick?: () => void
  className?: string
}

const AssessmentCard: React.FC<IProps> = ({
  type,
  assessment,
  isSubmitted,
  onClick,
  className,
}) => {
  const { theme } = useAppState()
  const { t } = useTranslation()

  const getIcon = () => {
    switch (type) {
      case 'goals':
        return 'Goals'
      case 'culture':
        return 'Culture'
      default:
        return 'Skills'
    }
  }

  const getTitle = () => {
    switch (type) {
      case 'skills':
        return t('general.skills')
      case 'goals':
        return t('general.goals.goals')
      case 'culture':
        return t('general.feedback.culture')
      default:
        return t('general.skills')
    }
  }

  const hasItems = () => {
    switch (type) {
      case 'skills':
        return assessment.skills.length > 0
      case 'goals':
        return assessment.goals.length > 0
      case 'culture':
        return assessment.culture.length > 0
      default:
        return false
    }
  }

  const getButtonCaption = () => {
    if (!isSubmitted && hasItems()) {
      return t('general.actions.continue')
    }

    return t('general.actions.start')
  }

  return (
    <AssessmentCardContainer className={className} onClick={onClick}>
      <AssessmentCardHeader>
        <div>
          <Text textStyle="labelTinyCaps" color="textLight">
            {t('general.assessment')}
          </Text>
          <Text textStyle="displayS">{getTitle()}</Text>
        </div>
        <StyledIcon type={getIcon()} size={24} color={theme.colors.primary} />
      </AssessmentCardHeader>
      <Button
        switchOrder
        icon="ChevronRight"
        variant={ButtonVariant.Text}
        caption={getButtonCaption()}
        color="primary"
        onClick={onClick}
      />
    </AssessmentCardContainer>
  )
}

const AssessmentCardContainer = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;

  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.textLightest};

  box-shadow: ${shadows.depthTwo};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryFadeLight};
    cursor: pointer;
    box-shadow: ${shadows.depthThree};
  }
`

const AssessmentCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const StyledIcon = styled(Icon)`
  align-self: baseline;
`

export default AssessmentCard
